import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Lock, CreditCard, LogOut } from 'lucide-react';
import api from '../../lib/api';
import { useAuth } from '../../hooks/useAuth';

/**
 * Full-screen gate that blocks the whole app when the organization is in
 * a "locked" status (trial expired, subscription ended, suspended by
 * admin, …). Users can still reach the billing page to upgrade, and can
 * log out — everything else is hidden.
 *
 * Mounted inside MainLayout so it only runs for authenticated org users.
 */
const LockGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [status, setStatus] = useState<string | null>(null);
  const [reason, setReason] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!user || !user.orgId) { setLoaded(true); return; }
    api.get('/organizations/me')
      .then(res => {
        setStatus(res.data?.status || null);
        setReason(res.data?.lockReason || null);
      })
      .catch(() => { /* leave ungated on fetch failure — don't trap users */ })
      .finally(() => setLoaded(true));
  }, [user, location.pathname]);

  const isLocked = status === 'locked' || status === 'suspended';
  const onBilling = location.pathname.startsWith('/billing');

  if (!loaded || !isLocked || onBilling) return <>{children}</>;

  const label = status === 'suspended' ? 'המערכת הושעתה' : 'המערכת ננעלה';
  const subtitle = status === 'suspended'
    ? 'החשבון שלך הושעה. ניתן ליצור קשר לתמיכה או לשדרג את החבילה.'
    : 'תקופת הניסיון הסתיימה או החבילה פגה. בחר חבילה כדי להמשיך להשתמש במערכת.';

  return (
    <div dir="rtl" className="fixed inset-0 z-[9998] bg-gradient-to-br from-red-50 via-orange-50 to-rose-100 dark:from-red-950/60 dark:via-red-900/40 dark:to-orange-950/60 flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white dark:bg-gray-900 rounded-3xl shadow-2xl border-2 border-red-200 dark:border-red-900 p-8 text-center space-y-5">
        <div className="mx-auto w-20 h-20 rounded-full bg-red-100 dark:bg-red-900/40 flex items-center justify-center">
          <Lock size={36} className="text-red-600 dark:text-red-400" />
        </div>
        <div>
          <h2 className="text-2xl font-extrabold text-gray-900 dark:text-white mb-2">{label}</h2>
          <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{subtitle}</p>
          {reason && (
            <p className="mt-3 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 rounded-lg px-3 py-2 border border-gray-200 dark:border-gray-700">
              <span className="font-bold">סיבה: </span>{reason}
            </p>
          )}
        </div>
        <div className="space-y-2 pt-2">
          <button
            onClick={() => navigate('/billing')}
            className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-xl shadow-lg transition-colors"
          >
            <CreditCard size={18} />
            לעמוד החיוב — שדרג/הפעל חבילה
          </button>
          <button
            onClick={() => { logout(); navigate('/login'); }}
            className="w-full flex items-center justify-center gap-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 font-semibold py-2.5 px-4 rounded-xl transition-colors text-sm"
          >
            <LogOut size={16} />
            התנתק
          </button>
        </div>
        <p className="text-[11px] text-gray-400 pt-2 border-t border-gray-100 dark:border-gray-800">
          נתקלת בבעיה? צור קשר לתמיכה: <a href="mailto:support@amutech.co.il" className="text-blue-600 hover:underline">support@amutech.co.il</a>
        </p>
      </div>
    </div>
  );
};

export default LockGate;
